import React, { useState, useRef, useEffect } from 'react';
import { DollarSign, Wallet, PieChart, Plus, Minus, ListPlus, ChevronDown } from 'lucide-react';
import { CATEGORY_COLORS } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';

interface Expense {
  id: string;
  name: string;
  amount: number;
  category: 'needs' | 'wants' | 'savings';
}

interface CategoryGroup {
  name: string;
  expenses: Expense[];
  totalAmount: number;
}

interface ExpenseListProps {
  category: 'needs' | 'wants' | 'savings';
  title: string;
  description?: string;
  expenses: Expense[];
  totalExpenses: number;
  onAddExpense: (category: 'needs' | 'wants' | 'savings') => void;
  onUpdateExpense: (id: string, field: 'name' | 'amount', value: string) => void;
  onRemoveExpense: (id: string) => void;
}

export const ExpenseList: React.FC<ExpenseListProps> = ({
  category,
  title,
  description,
  expenses,
  totalExpenses,
  onAddExpense,
  onUpdateExpense,
  onRemoveExpense
}) => {
  const colors = CATEGORY_COLORS[category];
  const categoryExpenses = expenses.filter(expense => expense.category === category);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Group expenses by name (first word before space)
  const groupedExpenses = categoryExpenses.reduce((groups, expense) => {
    const groupName = expense.name.split(' ')[0];
    if (!groups[groupName]) {
      groups[groupName] = {
        name: groupName,
        expenses: [],
        totalAmount: 0
      };
    }
    groups[groupName].expenses.push(expense);
    groups[groupName].totalAmount += expense.amount;
    return groups;
  }, {} as Record<string, CategoryGroup>);

  const expenseGroups = Object.values(groupedExpenses).sort((a, b) => b.totalAmount - a.totalAmount);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingId]);

  const handleInputBlur = (id: string, field: 'name' | 'amount') => {
    if (field === 'name') {
      onUpdateExpense(id, field, tempValue);
    }
    setEditingId(null);
    setTempValue('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, id: string, field: 'name' | 'amount') => {
    if (e.key === 'Enter') {
      if (field === 'name') {
        onUpdateExpense(id, field, tempValue);
      }
      setEditingId(null);
      setTempValue('');
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setTempValue('');
    }
  };

  return (
    <div className={`glass-card p-6 ${colors.border}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors.light}`}>
            {category === 'needs' ? <Wallet className={`w-5 h-5 ${colors.text}`} /> :
             category === 'wants' ? <PieChart className={`w-5 h-5 ${colors.text}`} /> :
             <DollarSign className={`w-5 h-5 ${colors.text}`} />}
          </div>
          <div>
            <h3 className="font-semibold text-lg">{title}</h3>
            {description && (
              <p className="text-sm text-slate-400">{description}</p>
            )}
          </div>
        </div>
        <button
          onClick={() => onAddExpense(category)}
          className={`px-3 py-2 rounded-lg ${colors.light} ${colors.text} hover:opacity-90 transition-opacity flex items-center gap-1 text-sm font-medium`}
        >
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>
      
      <div className="space-y-4 mt-6">
        {expenseGroups.map((group) => (
          <div key={group.name} className="glass-card p-4 border border-slate-700/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h4 className={`font-medium ${colors.text}`}>{group.name}</h4>
                <span className="text-sm text-slate-400">
                  ${group.totalAmount.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              {group.expenses.map(expense => (
                <div key={expense.id} className="glass-card p-3 hover:bg-slate-800/30 transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      {editingId === expense.id ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          onBlur={() => handleInputBlur(expense.id, 'name')}
                          onKeyDown={(e) => handleInputKeyDown(e, expense.id, 'name')}
                          className="w-full bg-transparent border-none focus:ring-0 text-slate-200 placeholder-slate-500"
                          placeholder="Expense name"
                        />
                      ) : (
                        <div
                          onClick={() => {
                            setEditingId(expense.id);
                            setTempValue(expense.name);
                          }}
                          className="cursor-text w-full py-1"
                        >
                          {expense.name}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="number"
                        value={expense.amount || ''}
                        onChange={(e) => onUpdateExpense(expense.id, 'amount', e.target.value)}
                        placeholder="0"
                        className="glass-input pl-9 pr-3 py-2 w-32 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-right"
                      />
                    </div>
                    <button
                      onClick={() => onRemoveExpense(expense.id)}
                      className={`p-2 rounded-lg ${colors.light} ${colors.text} hover:opacity-90 transition-opacity`}
                      title="Remove expense"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {categoryExpenses.length === 0 && (
          <div className={`text-center py-8 ${colors.text} opacity-60`}>
            <ListPlus className="w-12 h-12 mx-auto mb-3" />
            <p className="text-sm">No expenses added yet. Add your first expense to get started.</p>
          </div>
        )}
      </div>
      
      <div className="mt-6 pt-4 border-t border-slate-700">
        <div className="flex justify-between items-center">
          <span className="text-slate-400">Total Expenses</span>
          <span className={`font-medium ${colors.text}`}>
            ${totalExpenses.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};